import { collection, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db } from '@/lib/firebase'
import { OfflineCache } from '@/lib/offline-cache'
import { useConnectionState } from './use-connection-state'
import { useAuth } from '@/contexts/AuthContext'
import { useLicense } from './use-license'

export interface Signal {
  id: string
  symbol: string
  side: 'LONG' | 'SHORT'
  entry: number
  sl: number // stop loss
  tp: number // take profit
  rr: number // risk reward ratio
  status: 'active' | 'pending' | 'closed' | 'observation' | 'target-hit' | 'stop-hit'
  createdAt: Timestamp
  updatedAt: Timestamp
  timeframe?: string
  confidence?: number
  strategy?: string
  validity?: string
  pnlPercent?: number
}

export function useSignals() {
  const [signals, setSignals] = useState<Signal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isOfflineMode, setIsOfflineMode] = useState(false)
  const connectionState = useConnectionState()
  const { user } = useAuth()
  const { isPremium, isActive } = useLicense(user?.uid)

  useEffect(() => {
    if (!user) {
      setSignals([])
      setLoading(false)
      setIsOfflineMode(false)
      void OfflineCache.clearSignals()
      return
    }

    if (!isPremium || !isActive) {
      setSignals([])
      setLoading(false)
      setError(null)
      setIsOfflineMode(false)
      void OfflineCache.clearSignals()
      return
    }

    const loadCachedSignals = async () => {
      try {
        const cachedSignals = await OfflineCache.getCachedSignals(100)
        if (cachedSignals.length > 0) {
          // Convert CachedSignal to Signal
          const convertedSignals: Signal[] = cachedSignals.map(cached => ({
            id: cached.id,
            symbol: cached.symbol,
            side: cached.side,
            entry: cached.entry,
            sl: cached.sl,
            tp: cached.tp,
            rr: cached.rr,
            status: cached.status,
            createdAt: cached.createdAt,
            updatedAt: cached.updatedAt,
            timeframe: cached.timeframe,
            confidence: cached.confidence,
            strategy: cached.strategy,
            validity: cached.validity
          }))
          
          setSignals(convertedSignals)
          setIsOfflineMode(true)
          console.log(`Loaded ${convertedSignals.length} cached signals`)
        }
      } catch (error) {
        console.error('Error loading cached signals:', error)
      }
    }

    const setupRealtimeListener = () => {
      if (!db) {
        console.warn('Firestore is not initialized. Loading cached signals instead.')
        loadCachedSignals()
        setLoading(false)
        setIsOfflineMode(true)
        return () => {} // Return empty cleanup function
      }

      const signalsRef = collection(db, 'signals')
      const q = query(signalsRef, orderBy('createdAt', 'desc'))

      const unsubscribe = onSnapshot(
        q,
        async (snapshot) => {
          const signalsData: Signal[] = []
          snapshot.forEach((doc) => {
            signalsData.push({
              id: doc.id,
              ...doc.data()
            } as Signal)
          })
          
          setSignals(signalsData)
          setLoading(false)
          setError(null)
          setIsOfflineMode(false)

          // Cache the signals for offline use
          if (signalsData.length > 0) {
            await OfflineCache.cacheSignals(signalsData)
          }
        },
        (err) => {
          console.error('Error fetching signals:', err)
          const isPermissionError =
            err instanceof Error &&
            (err.message.toLowerCase().includes('permission') ||
              err.message.toLowerCase().includes('insufficient permissions'))

          if (isPermissionError) {
            setSignals([])
            setError(null)
            setIsOfflineMode(false)
            setLoading(false)
            return
          }

          setError('Failed to load signals')
          setLoading(false)
          
          // If online but Firestore fails, try to load cached data
          if (connectionState.isOnline) {
            loadCachedSignals()
          }
        }
      )

      return unsubscribe
    }

    // If offline, load cached signals immediately
    if (!connectionState.isOnline) {
      loadCachedSignals()
      setLoading(false)
      setIsOfflineMode(true)
      return
    }

    // If online, setup realtime listener
    const unsubscribe = setupRealtimeListener()

    return () => {
      unsubscribe()
    }
  }, [connectionState.isOnline, isActive, isPremium, user])

  // Update signal status in cache when it changes
  const updateSignalStatus = async (signalId: string, status: Signal['status']) => {
    try {
      await OfflineCache.updateSignalStatus(signalId, status)
      
      // Update local state
      setSignals(prev => prev.map(signal => 
        signal.id === signalId ? { ...signal, status } : signal
      ))
    } catch (error) {
      console.error('Error updating signal status:', error)
    }
  }

  return { 
    signals, 
    loading, 
    error, 
    isOfflineMode,
    connectionState,
    updateSignalStatus
  }
}
